import React from "react";
import {
  Input,
  Select,
  RadioGroup,
  Popover,
  Button,
  SpaceBetween,
} from "@amzn/awsui-components-console";
import {
  UslLine,
  UseCase,
  SubscriptionProductLine,
  UseCaseName,
  InvoiceAccountingClass,
} from "@amzn/fuse-accounting-central-datastore-client";
import { useValidationUtils } from "./ValidationUtils";
import { USL_LINE_KEY_TO_HEADER } from "src/components/constants/appConstants";
import { fieldPath } from "../hooks/useChangedFields";

/**
 * Inline style applied to a USL line cell whose value has been modified.
 * Uses `box-sizing: border-box` so the left border + padding don't push
 * the inner text past the cell bounds (which would otherwise trigger the
 * table's text-overflow ellipsis "...").
 */
const MODIFIED_CELL_STYLE: React.CSSProperties = {
  display: "inline-block",
  boxSizing: "border-box",
  width: "100%",
  borderLeft: "3px solid #0972d3",
  paddingLeft: "4px",
  backgroundColor: "rgba(9, 114, 211, 0.06)",
};

const wrapIfChanged = (
  node: React.ReactNode,
  changed: boolean,
): React.ReactNode =>
  changed ? <span style={MODIFIED_CELL_STYLE}>{node}</span> : node;

// Table Cell Component for USL Lines
interface TableCellProps {
  item: UslLine;
  fieldName: keyof UslLine;
  useCase: UseCase;
  validationUtils: ReturnType<typeof useValidationUtils>;
  onFocus?: (
    fieldName: string,
    useCaseName?: string | null,
    uslLineIndex?: number | null,
  ) => void;
  onBlur?: () => void;
  /** When true, render a "modified cell" indicator. */
  isChanged?: boolean;
}

export const TableCell: React.FC<TableCellProps> = ({
  item,
  fieldName,
  useCase,
  validationUtils,
  onFocus,
  onBlur,
  isChanged = false,
}) => {
  const { getFieldTooltipContent, getFieldTextColor } = validationUtils;
  const index = useCase.uslLines?.indexOf(item) || 0;
  const tooltipContent = getFieldTooltipContent(fieldName, useCase.name, index);
  const textColor = getFieldTextColor(fieldName, useCase.name, index);
  const value = item[fieldName] || "";

  if (tooltipContent) {
    return wrapIfChanged(
      <Popover
        renderWithPortal
        size="large"
        position="top"
        triggerType="text"
        dismissButton={false}
        content={tooltipContent}
      >
        <span
          style={{
            cursor: "pointer",
            width: "100%",
            display: "block",
            color: textColor,
          }}
        >
          {value}
        </span>
      </Popover>,
      isChanged,
    );
  }

  return wrapIfChanged(
    <span style={{ color: textColor }}>{value}</span>,
    isChanged,
  );
};

// Editing Cell Component for USL Lines
interface EditingCellProps {
  item: UslLine;
  fieldName: keyof UslLine;
  useCase: UseCase;
  currentValue: string;
  setValue: (value: string) => void;
  validationUtils: ReturnType<typeof useValidationUtils>;
  onFocus?: (
    fieldName: string,
    useCaseName?: string | null,
    uslLineIndex?: number | null,
  ) => void;
  onBlur?: () => void;
}

export const EditingCell: React.FC<EditingCellProps> = ({
  item,
  fieldName,
  useCase,
  currentValue,
  setValue,
  validationUtils,
  onFocus,
  onBlur,
}) => {
  const { getFieldTooltipContent, getFieldValidationProps, getFieldErrors } =
    validationUtils;
  const index = useCase.uslLines?.indexOf(item) || 0;
  const tooltipContent = getFieldTooltipContent(fieldName, useCase.name, index);
  const validationProps = getFieldValidationProps(
    fieldName,
    useCase.name,
    index,
  );

  const handleFocus = () => {
    // Only trigger focus for derived attributes (red fields)
    const errors = getFieldErrors(fieldName, useCase.name, index);
    const hasErrorField = errors.some((error) =>
      error.derivedAttributes?.includes(fieldName),
    );

    if (hasErrorField && onFocus) {
      onFocus(fieldName, useCase.name, index);
    }
  };

  const handleBlur = () => {
    if (onBlur) {
      onBlur();
    }
  };

  const inputComponent = (
    <div onFocus={handleFocus} onBlur={handleBlur}>
      <Input
        autoFocus
        value={currentValue ?? item[fieldName] ?? ""}
        onChange={(e) => setValue(e.detail.value)}
        {...validationProps}
      />
    </div>
  );

  if (tooltipContent) {
    return (
      <Popover
        renderWithPortal
        size="large"
        position="top"
        triggerType="custom"
        dismissButton={false}
        content={tooltipContent}
      >
        {inputComponent}
      </Popover>
    );
  }

  return inputComponent;
};

// Table column definitions factory
export const createTableColumnDefinitions = (
  useCase: UseCase,
  useCaseIndex: number,
  validationUtils: ReturnType<typeof useValidationUtils>,
  handleDuplicateUslLine: (useCaseIndex: number, lineIndex: number) => void,
  handleDeleteUslLineRequest: (useCaseIndex: number, lineIndex: number) => void,
  onFocus?: (
    fieldName: string,
    useCaseName?: string | null,
    uslLineIndex?: number | null,
  ) => void,
  onBlur?: () => void,
  isFieldChanged?: (path: string) => boolean,
  handleMoveUslLineUp?: (useCaseIndex: number, lineIndex: number) => void,
  handleMoveUslLineDown?: (useCaseIndex: number, lineIndex: number) => void,
) => {
  const isCellChanged = (item: UslLine, field: keyof UslLine): boolean => {
    if (!isFieldChanged) return false;
    const lineIndex = useCase.uslLines?.indexOf(item) ?? -1;
    if (lineIndex < 0) return false;
    return isFieldChanged(fieldPath.uslLine(useCaseIndex, lineIndex, field));
  };
  const freeTextColumns = (
    [
      { id: "companyCode" },
      { id: "location" },
      { id: "costCenter" },
      { id: "accountNumber" },
      { id: "productLine" },
      { id: "channel" },
      { id: "project" },
      { id: "glLineDescriptionSuffix" },
    ] as { id: keyof UslLine }[]
  )
    .map(({ id }) => ({ id, header: USL_LINE_KEY_TO_HEADER[id] }))
    .map(({ id, header }) => ({
      id,
      header,
      minWidth: 130,
      cell: (item: UslLine) => (
        <TableCell
          item={item}
          fieldName={id}
          useCase={useCase}
          validationUtils={validationUtils}
          onFocus={onFocus}
          onBlur={onBlur}
          isChanged={isCellChanged(item, id)}
        />
      ),
      editConfig: {
        ariaLabel: header,
        editIconAriaLabel: `Edit ${header}`,
        editingCell: (item: UslLine, { currentValue, setValue }: any) => (
          <EditingCell
            item={item}
            fieldName={id}
            useCase={useCase}
            currentValue={currentValue}
            setValue={setValue}
            validationUtils={validationUtils}
            onFocus={onFocus}
            onBlur={onBlur}
          />
        ),
      },
    }));

  const standardColumns = freeTextColumns.filter(
    ({ id }) => id !== "glLineDescriptionSuffix",
  );
  const glLineDescriptionColumn = freeTextColumns.find(
    ({ id }) => id === "glLineDescriptionSuffix",
  );

  const debitCreditColumn = {
    id: "debitOrCredit",
    header: "Debit/Credit",
    cell: (item: UslLine) =>
      wrapIfChanged(
        item.debitOrCredit || "",
        isCellChanged(item, "debitOrCredit"),
      ),
    editConfig: {
      ariaLabel: "Debit or Credit",
      editIconAriaLabel: "Edit Debit or Credit",
      editingCell: (item: UslLine, { currentValue, setValue }: any) => (
        <RadioGroup
          value={currentValue || item.debitOrCredit || null}
          onChange={({ detail }) => setValue(detail.value)}
          items={[
            { value: "debit", label: "Debit" },
            { value: "credit", label: "Credit" },
          ]}
        />
      ),
    },
  };

  const [subscriptionProductLineColumn, invoiceAccountingClassColumn] = (
    [
      {
        id: "subscriptionProductLine",
        options: Object.values(SubscriptionProductLine),
      },
      {
        id: "invoiceAccountingClass",
        options: Object.values(InvoiceAccountingClass),
      },
    ] as { id: keyof UslLine; options: string[] }[]
  )
    .map(({ id, options }) => ({
      id,
      options,
      header: USL_LINE_KEY_TO_HEADER[id],
    }))
    .map(({ id, options, header }) => ({
      id,
      header,
      cell: (item: UslLine) =>
        wrapIfChanged(item[id] || "", isCellChanged(item, id)),
      editConfig: {
        ariaLabel: header,
        editIconAriaLabel: `Edit ${header}`,
        editingCell: (item: UslLine, { currentValue, setValue }: any) => (
          <Select
            selectedOption={
              currentValue
                ? { label: currentValue, value: currentValue }
                : item[id]
                  ? {
                      label: item[id],
                      value: item[id],
                    }
                  : null
            }
            onChange={({ detail }) =>
              setValue(detail.selectedOption?.value || "")
            }
            options={options.map((v) => ({
              label: v,
              value: v,
            }))}
            placeholder={`Select ${header}`}
            expandToViewport
          />
        ),
      },
    }));

  const postLocalToGLColumn = {
    id: "postLocalToGeneralLedger",
    header: USL_LINE_KEY_TO_HEADER["postLocalToGeneralLedger"],
    cell: (item: UslLine) => String(item.postLocalToGeneralLedger ?? false),
    editConfig: {
      ariaLabel: "Post Local to GL",
      editIconAriaLabel: "Edit Post Local to GL",
      editingCell: (item: UslLine, { currentValue, setValue }: any) => (
        <Select
          selectedOption={
            currentValue != null
              ? { label: currentValue, value: currentValue }
              : {
                  label: String(item.postLocalToGeneralLedger ?? false),
                  value: String(item.postLocalToGeneralLedger ?? false),
                }
          }
          onChange={({ detail }) =>
            setValue(detail.selectedOption?.value || "")
          }
          options={[
            { label: "true", value: "true" },
            { label: "false", value: "false" },
          ]}
          placeholder="Select value"
          expandToViewport
        />
      ),
    },
  };

  const actionsColumn = {
    id: "actions",
    header: "Actions",
    minWidth: 200,
    cell: (item: UslLine) => {
      const index = useCase.uslLines?.indexOf(item) || 0;
      const totalLines = useCase.uslLines?.length || 0;
      return (
        <SpaceBetween direction="horizontal" size="xs">
          <Button
            iconName="angle-up"
            variant="icon"
            onClick={() => handleMoveUslLineUp?.(useCaseIndex, index)}
            disabled={index === 0}
            ariaLabel="Move line up"
          />
          <Button
            iconName="angle-down"
            variant="icon"
            onClick={() => handleMoveUslLineDown?.(useCaseIndex, index)}
            disabled={index === totalLines - 1}
            ariaLabel="Move line down"
          />
          <Button
            iconName="copy"
            variant="icon"
            onClick={() => handleDuplicateUslLine(useCaseIndex, index)}
            ariaLabel="Duplicate line"
          />
          <Button
            iconName="remove"
            variant="icon"
            onClick={() => handleDeleteUslLineRequest(useCaseIndex, index)}
            disabled={useCase.uslLines && useCase.uslLines.length <= 2}
            ariaLabel="Delete line"
          />
        </SpaceBetween>
      );
    },
  };

  if (useCase.name === UseCaseName.INVOICE) {
    return [
      ...standardColumns,
      debitCreditColumn,
      invoiceAccountingClassColumn,
      postLocalToGLColumn,
      actionsColumn,
    ];
  }

  return [
    ...standardColumns,
    debitCreditColumn,
    glLineDescriptionColumn,
    subscriptionProductLineColumn,
    postLocalToGLColumn,
    actionsColumn,
  ];
};
